import { useQuery } from "@tanstack/react-query";

/**
 * Custom hook untuk implementasi stale-while-revalidate
 *
 * Strategi:
 * 1. Tampilkan cache terlebih dahulu (instant load)
 * 2. Fetch data baru di background
 * 3. Update UI ketika data baru tersedia
 *
 * @param {string|Array} queryKey - Key untuk query
 * @param {Function} queryFn - Function untuk fetch data
 * @param {Object} options - Opsi tambahan
 * @param {number} options.staleTime - Waktu data dianggap fresh (default: 5 menit)
 * @param {number} options.cacheTime - Waktu data disimpan di cache (default: 15 menit)
 * @param {boolean} options.refetchOnMount - Refetch saat mount (default: true)
 * @param {number|false} options.refetchInterval - Auto refetch interval (default: false)
 *
 * @example
 * const { data, isLoading, isFetching, isStale } = useStaleWhileRevalidate(
 *   ["units", id],
 *   () => fetchUnitData(id),
 *   { staleTime: 1000 * 60 * 3 } // 3 menit
 * );
 */
export const useStaleWhileRevalidate = (queryKey, queryFn, options = {}) => {
  const {
    staleTime = 0, // Default 0 - langsung stale, akan refetch di background
    cacheTime = 1000 * 60 * 15, // 15 menit default
    refetchOnMount = true,
    refetchInterval = false,
    ...restOptions
  } = options;

  const query = useQuery(queryKey, queryFn, {
    staleTime,
    cacheTime,
    refetchOnMount,
    refetchInterval,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    ...restOptions,
  });

  return {
    ...query,
    // Helper untuk mengetahui apakah sedang fetch di background
    isBackgroundFetching: query.isFetching && !query.isLoading,
    // Helper untuk mengetahui apakah data sudah stale
    isStale: query.isStale,
  };
};

/**
 * Hook untuk data yang jarang berubah (misal: master data, config)
 * Cache lebih lama, stale time lebih lama
 */
export const useStaticData = (queryKey, queryFn, options = {}) => {
  return useStaleWhileRevalidate(queryKey, queryFn, {
    staleTime: 1000 * 60 * 30, // 30 menit - tidak perlu sering refetch
    cacheTime: 1000 * 60 * 60 * 24 * 7, // 7 hari
    refetchOnMount: false, // Tidak perlu refetch setiap mount untuk master data
    ...options,
  });
};

/**
 * Hook untuk data yang sering berubah (misal: live auction, notifikasi)
 * Stale time lebih pendek, auto refetch
 */
export const useLiveData = (queryKey, queryFn, options = {}) => {
  return useStaleWhileRevalidate(queryKey, queryFn, {
    staleTime: 0, // Langsung stale - selalu refetch
    cacheTime: 1000 * 60 * 5, // 5 menit
    refetchInterval: 1000 * 60, // Auto refetch setiap 1 menit
    refetchOnMount: true,
    ...options,
  });
};

/**
 * Hook untuk data yang perlu fresh tapi tetap pakai cache
 * Balance antara performance dan freshness
 *
 * Sesuai Cache Duration.md:
 * - Unit Lelang: 15 menit
 * - Jadwal Lelang: 5 menit
 * - Detail Unit: 5 menit
 */
export const useBalancedData = (queryKey, queryFn, options = {}) => {
  // Default cache time 5 menit (untuk Jadwal Lelang & Detail Unit)
  const defaultCacheTime = 1000 * 60 * 5;

  return useStaleWhileRevalidate(queryKey, queryFn, {
    staleTime: 0, // Langsung stale - akan refetch di background setiap mount
    cacheTime: defaultCacheTime,
    refetchOnMount: true, // Selalu refetch di background saat mount
    ...options,
  });
};

/**
 * Hook khusus untuk Unit Lelang dengan cache 15 menit
 */
export const useUnitLelangData = (queryKey, queryFn, options = {}) => {
  return useStaleWhileRevalidate(queryKey, queryFn, {
    staleTime: 0,
    cacheTime: 1000 * 60 * 15, // 15 menit sesuai spesifikasi
    refetchOnMount: true,
    ...options,
  });
};
